import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-cropper';
import "cropperjs/dist/cropper.css";
import { X } from 'lucide-react';

/**
 * 简洁的图片裁剪弹窗
 * 用于参考图上传：正方形裁剪 + 标题输入
 *
 * Props:
 * - imageUrl: 待裁剪的图片 URL
 * - onSave: (croppedBlob, title) => void
 * - onCancel: () => void
 */
export default function SimpleImageCropModal({ imageUrl, onSave, onCancel }) {
  const cropperRef = useRef(null);
  const [title, setTitle] = useState('');
  const [safeSrc, setSafeSrc] = useState(null);
  const blobUrlRef = useRef(null);

  // 预转换为 blob URL 避免跨域问题
  useEffect(() => {
    if (!imageUrl) return;

    // blob/data URL 直接使用
    if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
      setSafeSrc(imageUrl);
      return;
    }

    // 远程 URL → fetch → blob URL
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);

    fetch(imageUrl, { signal: controller.signal })
      .then(r => {
        clearTimeout(timer);
        if (!r.ok) throw new Error(r.status);
        return r.blob();
      })
      .then(blob => {
        if (!cancelled) {
          const blobUrl = URL.createObjectURL(blob);
          blobUrlRef.current = blobUrl;
          setSafeSrc(blobUrl);
        }
      })
      .catch(err => {
        console.warn('[SimpleImageCropModal] 图片加载失败:', err);
        if (!cancelled) setSafeSrc(imageUrl); // 降级使用原始 URL
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [imageUrl]);

  const handleConfirm = () => {
    if (!cropperRef.current?.cropper) return;
    if (!title.trim()) {
      alert('请输入标题');
      return;
    }

    try {
      const canvas = cropperRef.current.cropper.getCroppedCanvas({
        width: 400,
        height: 400,
        imageSmoothingQuality: 'high'
      });

      if (canvas) {
        canvas.toBlob((blob) => {
          if (blob) {
            onSave(blob, title.trim());
          }
        }, 'image/jpeg', 0.9);
      }
    } catch (err) {
      console.error('[SimpleImageCropModal] 裁剪失败:', err);
      alert('裁剪失败，请重试');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          width: '500px',
          background: 'var(--bg-panel)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '16px',
          boxShadow: '0 25px 60px rgba(0,0,0,0.9)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'var(--save-header-bg)'
        }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>
            上传参考图
          </div>
          <X
            size={20}
            style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
            onClick={onCancel}
          />
        </div>

        {/* 裁剪区域 */}
        <div style={{
          height: '400px',
          position: 'relative',
          background: '#1a1a1a',
        }}>
          {safeSrc ? (
            <Cropper
              src={safeSrc}
              style={{ height: '100%', width: '100%' }}
              aspectRatio={1}  // 固定 1:1 正方形
              guides={true}
              ref={cropperRef}
              viewMode={1}
              dragMode="move"
              autoCrop={true}
              background={false}
              autoCropArea={0.9}
              checkCrossOrigin={true}
              zoomOnWheel={true}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--text-secondary)'
            }}>
              加载图片中…
            </div>
          )}
        </div>

        {/* 标题输入 */}
        <div style={{ padding: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            color: 'var(--text-secondary)',
            marginBottom: '8px',
            fontWeight: 'bold'
          }}>
            参考图标题
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="输入标题（例如：现代简约风格）"
            autoFocus
            style={{
              width: '100%',
              padding: '12px',
              background: '#09090b',
              border: '1px solid #333',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={e => e.target.style.borderColor = '#3b82f6'}
            onBlur={e => e.target.style.borderColor = '#333'}
          />
        </div>

        {/* 底部按钮 */}
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          background: 'var(--bg-panel)'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid var(--border-strong)',
              background: 'transparent',
              color: 'var(--text-body)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={!title.trim()}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: title.trim() ? '#3b82f6' : '#334155',
              color: '#fff',
              cursor: title.trim() ? 'pointer' : 'not-allowed',
              fontSize: '13px',
              fontWeight: 500,
              opacity: title.trim() ? 1 : 0.6,
            }}
          >
            确认上传
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
